<script setup>
import { isNumber } from 'lodash'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import SSOApiHandler from '@/api/sso'
import { DIALOG_TYPES } from '@/utils/components-configurations/app-dialog'
import { SAML_CONNECTION_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { isStringEmpty } from '@/utils/general'
import { objectOrNullProp } from '@/utils/prop-validators'
import { SAML_TITLE } from '@/utils/sso-connection-settings'

import AppDialog from '@/components/AppDialog'
import AppFieldError from '@/components/form/AppFieldError'
import FormFieldNext from '@/components/form/FormFieldNext'
import AppInput from '@/components/ui/AppInput/AppInput'
import AppTextarea from '@/components/ui/AppTextarea/AppTextarea'

defineOptions({
  name: 'CertificateModal'
})

const props = defineProps({
  connection: {
    validator: v => objectOrNullProp(v),
    required: true
  }
})

const { t } = useI18n()

const showModal = defineModel('show', {
  type: Boolean
})

const loading = ref(false)

const emit = defineEmits({
  'reload-connections': null,
  'setup-activation': null
})

const closeModal = (shouldActivateConnection = false) => {
  showModal.value = false

  if (shouldActivateConnection) {
    emit('setup-activation', props.connection[SAML_CONNECTION_ENTITY_KEYS.ID])
  } else {
    emit('reload-connections')
  }
  issuer.value = ''
  ssoEndpoint.value = ''
  certificate.value = ''
  issuerError.value = null
  ssoEndpointError.value = null
  certificateError.value = null
}

const ssoEndpoint = ref('')
const issuer = ref('')
const certificate = ref('')

const ssoEndpointError = ref(null)
const issuerError = ref(null)
const certificateError = ref(null)

const ERROR_CODE_1 = 1
const ERROR_CODE_2 = 2

const api = new SSOApiHandler()

const onConfirm = async () => {
  const isSsoEndpointEmpty = isStringEmpty(ssoEndpoint.value)
  const isCertificateEmpty = isStringEmpty(certificate.value)
  const isIssuerEmpty = isStringEmpty(issuer.value)

  if (isIssuerEmpty) {
    issuerError.value = t('field.required')
  }

  if (isSsoEndpointEmpty) {
    ssoEndpointError.value = t('field.required')
  }

  if (isCertificateEmpty) {
    certificateError.value = t('field.required')
  }

  if (isSsoEndpointEmpty || isCertificateEmpty || isIssuerEmpty) {
    return
  }

  loading.value = true

  try {
    await api.addCertificateToSamlConnection({
      id: props.connection.id,
      ssoEndpoint: ssoEndpoint.value,
      certificate: certificate.value,
      issuer: issuer.value
    })

    closeModal(true)
  } catch (error) {
    const errorCode = error.response?.data?.errorCode
    if (isNumber(errorCode)) {
      const errors = {
        [ERROR_CODE_1]: t('sso.error_type_1'),
        [ERROR_CODE_2]: t('sso.error_type_2')
      }

      ssoEndpointError.value = errors[errorCode]

      if (errorCode === ERROR_CODE_2) {
        certificateError.value = errors[errorCode]
      }
    } else {
      handleError({ error })
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <AppDialog
    :loading-confirm-button="loading"
    :show="showModal"
    :type="DIALOG_TYPES.INFO"
    hide-hero
    :title="SAML_TITLE"
    @on-close="closeModal"
    @on-confirm="onConfirm"
  >
    <div class="cm-Wrapper">
      <FormFieldNext :label="$t('sso.acs_url')">
        <AppInput
          :model-value="connection.acsUrl"
          copyable
          readonly
          size="xlg"
          style-type="primary"
        />
      </FormFieldNext>

      <FormFieldNext :label="$t('integration_settings.sp_entity_id')">
        <AppInput
          :model-value="connection.spEntityId"
          copyable
          readonly
          size="xlg"
          style-type="primary"
        />
      </FormFieldNext>

      <FormFieldNext :label="$t('sso.issuer')" required>
        <AppInput
          v-model="issuer"
          :is-error="Boolean(issuerError)"
          :max-length="2048"
          :placeholder="$t('sso.enter_issuer')"
          size="xlg"
          style-type="primary"
          @focus="issuerError = null"
        />
        <AppFieldError v-if="issuerError" :show="Boolean(issuerError)">
          {{ issuerError }}
        </AppFieldError>
      </FormFieldNext>

      <FormFieldNext :label="$t('sso.sso_endpoint')" required>
        <AppInput
          v-model="ssoEndpoint"
          :is-error="Boolean(ssoEndpointError)"
          :max-length="2048"
          :placeholder="$t('sso.enter_sso')"
          size="xlg"
          style-type="primary"
          @focus="ssoEndpointError = null"
        />
        <AppFieldError v-if="ssoEndpointError" :show="Boolean(ssoEndpointError)">
          {{ ssoEndpointError }}
        </AppFieldError>
      </FormFieldNext>

      <FormFieldNext :label="$t('sso.certificate')" required>
        <AppTextarea
          v-model="certificate"
          :is-error="Boolean(certificateError)"
          :max-length="6000"
          :placeholder="$t('sso.enter_certificate')"
          @focus="certificateError = null"
        >
          <template #error-text>{{ certificateError }}</template>
        </AppTextarea>
      </FormFieldNext>
    </div>
  </AppDialog>
</template>

<style scoped lang="scss">
.cm-Wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
</style>
